import { NgForOf } from '@angular/common';
import {
  AfterViewInit,
  Directive,
  DoCheck,
  ElementRef,
  EventEmitter,
  Host,
  Input,
  OnDestroy,
  Output,
} from '@angular/core';

interface SortTransferData {
  index: number;
  offsetTop: number;
  offsetHeight: number;
}

@Directive({
  selector: '[dragSort]',
  standalone: false,
})
export class DragSortDirective<T> implements DoCheck, AfterViewInit, OnDestroy {
  @Input() dragSort: T;
  @Input() dragSortBy: keyof T;

  @Output() dropped = new EventEmitter<{ item: T; sort: number }>();

  private isDragging = false;

  private dropTarget: HTMLElement;
  private dropTargetVisible = false;
  private dragEnterTarget?: HTMLElement;

  private static currentDrag?: NgForOf<any>;

  constructor(
    private el: ElementRef<HTMLElement>,
    @Host() private ngForOf: NgForOf<T>,
  ) {}

  ngAfterViewInit() {
    this.el.nativeElement.addEventListener('dragstart', this.onDragStart);
    this.el.nativeElement.addEventListener('dragend', this.onDragEnd);

    const parent = this.el.nativeElement.parentElement;

    if (!parent) {
      throw new Error('No parent element');
    }

    this.dropTarget = document.createElement(this.el.nativeElement.tagName);

    this.dropTarget.hidden = true;
    this.dropTarget.classList.add('is-drop-target');

    if (this.el.nativeElement.tagName === 'TR') {
      const colspan = Array.from(this.el.nativeElement.children).reduce(
        (acc, c) => acc + ((c as HTMLTableCellElement).colSpan ?? 1),
        0,
      );

      const dropTargetCell = document.createElement(
        this.el.nativeElement.children[0].tagName,
      ) as HTMLTableCellElement;
      dropTargetCell.colSpan = colspan;

      this.dropTarget.appendChild(dropTargetCell);
    }

    parent.insertBefore(this.dropTarget, this.el.nativeElement);

    this.dropTarget.addEventListener('dragenter', this.onDragEnterTarget);
    this.dropTarget.addEventListener('dragover', this.onDragOverTarget);
    this.dropTarget.addEventListener('dragleave', this.onDragLeaveTarget);
    this.dropTarget.addEventListener('drop', this.onDrop);
  }

  ngOnDestroy() {
    this.el.nativeElement.removeEventListener('dragstart', this.onDragStart);
    this.el.nativeElement.removeEventListener('dragend', this.onDragEnd);

    this.dropTarget.removeEventListener('dragenter', this.onDragEnterTarget);
    this.dropTarget.removeEventListener('dragover', this.onDragOverTarget);
    this.dropTarget.removeEventListener('dragleave', this.onDragLeaveTarget);
    this.dropTarget.removeEventListener('drop', this.onDrop);
  }

  ngDoCheck() {
    const shouldHaveVisibleDropTarget =
      DragSortDirective.currentDrag === this.ngForOf && !this.isDragging;

    if (shouldHaveVisibleDropTarget !== this.dropTargetVisible) {
      if (shouldHaveVisibleDropTarget) {
        const height = this.el.nativeElement.clientHeight;
        this.dropTarget.hidden = false;
        this.dropTarget.style.setProperty('--target-height', height + 'px');
        this.dropTargetVisible = true;
      } else {
        this.dropTarget.hidden = true;
        this.dropTargetVisible = false;
      }
    }
  }

  private getIndexes(event: DragEvent) {
    if (!event.dataTransfer) {
      throw new Error();
    }

    const {
      index: fromIndex,
      offsetTop,
      offsetHeight,
    } = JSON.parse(event.dataTransfer.getData('application/json')) as SortTransferData;

    const list = (this.ngForOf as any)._ngForOf as T[];

    const toIndex = list.indexOf(this.dragSort);

    let targetSort: number | null = null;
    if (toIndex != null) {
      const afterIndex = fromIndex < toIndex ? toIndex + 1 : toIndex;
      if (afterIndex === 0) {
        targetSort = +this.dragSort[this.dragSortBy] - 50;
      } else if (afterIndex === list.length) {
        targetSort = +this.dragSort[this.dragSortBy] + 50;
      } else {
        const sort1 = +list[afterIndex - 1][this.dragSortBy];
        const sort2 = +list[afterIndex][this.dragSortBy];
        if (sort2 - sort1 > 1) {
          targetSort = Math.round((sort1 + sort2) / 2);
        }
      }
    }

    return {
      fromIndex,
      offsetTop,
      offsetHeight,
      toIndex,
      targetSort,
    };
  }

  private onDragStart = (event: DragEvent) => {
    if (event.dataTransfer && this.el.nativeElement.parentElement) {
      DragSortDirective.currentDrag = this.ngForOf;

      this.isDragging = true;

      event.dataTransfer.setData(
        'application/json',
        JSON.stringify({
          index: (this.ngForOf as any)._ngForOf.indexOf(this.dragSort),
          offsetTop: this.el.nativeElement.offsetTop,
          offsetHeight: this.el.nativeElement.offsetHeight,
        } as SortTransferData),
      );
      event.dataTransfer.effectAllowed = 'move';

      this.el.nativeElement.classList.add('is-dragging');
      this.el.nativeElement.parentElement?.style.setProperty(
        '--drag-height',
        this.el.nativeElement.offsetHeight + 'px',
      );
      event.dataTransfer.setDragImage(document.createElement('span'), 0, 0);
    }
  };

  private onDragEnd = () => {
    DragSortDirective.currentDrag = undefined;
    this.isDragging = false;
    this.el.nativeElement.classList.remove('is-dragging');
    this.el.nativeElement.parentElement?.style.removeProperty('--drag-height');
  };

  private onDragEnterTarget = (event: DragEvent) => {
    if (!this.isDragging && event.dataTransfer) {
      this.dragEnterTarget = event.target as HTMLElement;

      const { fromIndex, toIndex, targetSort, offsetTop, offsetHeight } = this.getIndexes(event);

      this.el.nativeElement.parentElement?.style.removeProperty('--offset-y');
      if (fromIndex !== -1 && toIndex != null && fromIndex !== toIndex && targetSort != null) {
        event.preventDefault();
        event.dataTransfer.dropEffect = 'move';

        setTimeout(() => {
          const { offsetTop: targetOffsetTop, offsetHeight: targetOffsetHeight } =
            this.el.nativeElement;
          const offsetY =
            targetOffsetTop > offsetTop
              ? targetOffsetTop - offsetTop + targetOffsetHeight - offsetHeight
              : targetOffsetTop - offsetTop;

          this.el.nativeElement.parentElement?.style.setProperty('--offset-y', offsetY + 'px');
          this.dropTarget.classList.add('is-drag-over');
        });
      }
    }
  };

  private onDragOverTarget = (event: DragEvent) => {
    if (!this.isDragging && event.dataTransfer) {
      const { fromIndex, toIndex, targetSort } = this.getIndexes(event);

      if (fromIndex !== -1 && fromIndex !== toIndex && targetSort != null) {
        event.preventDefault();
      }
    }
  };

  private onDragLeaveTarget = (event: DragEvent) => {
    if (!this.isDragging) {
      this.dropTarget.classList.remove('is-drag-over');

      if (this.dragEnterTarget === event.target) {
        this.dropTarget.hidden = true;
        this.dropTargetVisible = false;
        this.dragEnterTarget = undefined;
        this.el.nativeElement.parentElement?.style.removeProperty('--offset-y');
      }
    }
  };

  private onDrop = (event: DragEvent) => {
    if (!this.isDragging && event.dataTransfer) {
      const { fromIndex, toIndex, targetSort } = this.getIndexes(event);

      const list = (this.ngForOf as any)._ngForOf as T[];

      this.dropTarget.classList.remove('is-drag-over');
      this.dropTarget.hidden = true;
      this.dropTargetVisible = false;
      this.dragEnterTarget = undefined;

      if (toIndex != null && toIndex >= 0 && targetSort != null) {
        const [item] = list.splice(fromIndex, 1);
        list.splice(toIndex, 0, item);

        this.dropped.emit({ item, sort: targetSort });
      }
    }
  };
}
